import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  cardApprovalPutSchema,
  cardApprovalsSchema,
  type ApiError,
  type ApprovalVerdict,
  type CardApprovals,
} from '@molt/shared';

import { del, emptyResponseSchema, post, put } from './api';
import { queryKeys } from './query-keys';

export interface SetCardApprovalVariables {
  readonly boardId: string;
  readonly cardId: string;
  readonly verdict: ApprovalVerdict;
  readonly reason: string | null;
}

export interface WithdrawCardApprovalVariables {
  readonly boardId: string;
  readonly cardId: string;
}

export interface ResetCardApprovalsVariables {
  readonly boardId: string;
  readonly cardId: string;
}

/** `PUT /cards/:id/approval` — the caller's own verdict, upserted. */
export function setCardApproval(
  cardId: string,
  body: { verdict: ApprovalVerdict; reason: string | null },
): Promise<CardApprovals> {
  return put(
    `/cards/${encodeURIComponent(cardId)}/approval`,
    cardApprovalsSchema,
    // `parse` applies the server's trim / empty-to-null rule to the wire body.
    cardApprovalPutSchema.parse(body),
  );
}

/** `DELETE /cards/:id/approval` — idempotent `204`. */
export function withdrawCardApproval(cardId: string): Promise<null> {
  return del(`/cards/${encodeURIComponent(cardId)}/approval`, emptyResponseSchema);
}

/**
 * `POST /cards/:id/approvals/reset` — removes EVERY member's verdict on the
 * card, not only the caller's, for a card that needs more work and must be
 * revalidated. No body. Returns the emptied approvals block; a repeat reset is
 * an idempotent `200`.
 */
export function resetCardApprovals(cardId: string): Promise<CardApprovals> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/approvals/reset`,
    cardApprovalsSchema,
  );
}

function refreshApprovalReaders(
  queryClient: QueryClient,
  boardId: string,
  cardId: string,
): void {
  // The card back's `approvals` block and the card front's `approvals` badge.
  void queryClient.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.contents(boardId) });
}

export function useSetCardApproval(): UseMutationResult<
  CardApprovals,
  ApiError,
  SetCardApprovalVariables
> {
  const queryClient = useQueryClient();

  return useMutation<CardApprovals, ApiError, SetCardApprovalVariables>({
    mutationFn: (variables) =>
      setCardApproval(variables.cardId, {
        verdict: variables.verdict,
        reason: variables.reason,
      }),
    onSuccess: (_approvals, variables) => {
      refreshApprovalReaders(queryClient, variables.boardId, variables.cardId);
    },
  });
}

export function useWithdrawCardApproval(): UseMutationResult<
  null,
  ApiError,
  WithdrawCardApprovalVariables
> {
  const queryClient = useQueryClient();

  return useMutation<null, ApiError, WithdrawCardApprovalVariables>({
    mutationFn: (variables) => withdrawCardApproval(variables.cardId),
    onSuccess: (_data, variables) => {
      refreshApprovalReaders(queryClient, variables.boardId, variables.cardId);
    },
  });
}

export function useResetCardApprovals(): UseMutationResult<
  CardApprovals,
  ApiError,
  ResetCardApprovalsVariables
> {
  const queryClient = useQueryClient();

  return useMutation<CardApprovals, ApiError, ResetCardApprovalsVariables>({
    mutationFn: (variables) => resetCardApprovals(variables.cardId),
    onSuccess: (_approvals, variables) => {
      refreshApprovalReaders(queryClient, variables.boardId, variables.cardId);
    },
  });
}
